import React, { useEffect, useState, useRef, useReducer } from 'react';
import { PAGE_ROUTE } from '../../lib/routes';
import { saveOnboardingDraft, loadOnboardingDraft, clearOnboardingDraft, setSession, updateSession, getSession, setRecentPublish } from '../../lib/utils';
import { OnboardingDraft, OnboardingAction } from './onboarding/types';
import { StepLivingModel } from './onboarding/StepLivingModel';
import { StepListingType } from './onboarding/StepListingType';
import { StepLocationBudget } from './onboarding/StepLocationBudget';
import { StepTiming } from './onboarding/StepTiming';
import { StepLifestyle } from './onboarding/StepLifestyle';
import { StepBioAmenities } from './onboarding/StepBioAmenities';
import { StepAccount } from './onboarding/StepAccount';
import { StepProfile } from './onboarding/StepProfile';
import { OnboardingHeader } from './onboarding/OnboardingHeader';
import { OnboardingProgressBar } from './onboarding/OnboardingProgressBar';
import { Button } from '../ui/Button';
import { ConfirmDialog } from '../ui/ConfirmDialog';
const initialState: OnboardingDraft = {
  stage: 'anunt',
  subStep: 0
};
// Reducer with new stage order: anunț → profil → cont
function reducer(state: OnboardingDraft, action: OnboardingAction): OnboardingDraft {
  switch (action.type) {
    case 'SET_STAGE':
      return {
        ...state,
        stage: action.stage,
        subStep: (action.subStep || 0) as any
      };
    case 'NEXT_STEP':
      if (state.stage === 'anunt') {
        if (state.subStep < 5) return {
          ...state,
          subStep: state.subStep + 1 as any
        };
        return {
          ...state,
          stage: 'profil',
          subStep: 0
        };
      }
      if (state.stage === 'profil') return {
        ...state,
        stage: 'cont',
        subStep: 0
      };
      // cont is the last stage in normal flow; finalization is external
      return state;
    case 'PREV_STEP':
      if (state.stage === 'anunt' && state.subStep > 0) {
        return {
          ...state,
          subStep: state.subStep - 1 as any
        };
      }
      if (state.stage === 'profil') return {
        ...state,
        stage: 'anunt',
        subStep: 5
      };
      if (state.stage === 'cont') return {
        ...state,
        stage: 'profil',
        subStep: 0
      };
      return state;
    case 'UPDATE_DATA':
      return {
        ...state,
        ...action.payload
      };
    case 'LOAD_DRAFT':
      return action.payload;
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}
interface OnboardingWizardProps {
  navigate: (page: string, context?: any) => void;
}
export function OnboardingWizard({
  navigate
}: OnboardingWizardProps) {
  // Detect listing-only mode (user is already authenticated → skip Profil + Cont)
  const sessionRef = useRef(getSession());
  const listingOnly = !!sessionRef.current;
  const [state, dispatch] = useReducer(reducer, initialState);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  // Load draft on mount; clamp stage in listing-only mode
  useEffect(() => {
    const draft = loadOnboardingDraft();
    if (!draft || !draft.stage) return;
    if (listingOnly) {
      const safeSubStep = Math.min(draft.subStep ?? 0, 5);
      dispatch({
        type: 'LOAD_DRAFT',
        payload: {
          ...draft,
          stage: 'anunt',
          subStep: safeSubStep as any
        }
      });
      if (safeSubStep > 0) setShowDraftBanner(true);
    } else {
      if (draft.stage !== 'anunt' || draft.subStep > 0) {
        setShowDraftBanner(true);
        dispatch({
          type: 'LOAD_DRAFT',
          payload: draft
        });
      }
    }
  }, [listingOnly]);
  // Auto-save draft on every state change
  useEffect(() => {
    saveOnboardingDraft({
      ...state,
      draftedAt: new Date().toISOString()
    });
  }, [state]);
  const handleReset = () => {
    setShowResetDialog(true);
  };
  const confirmReset = () => {
    clearOnboardingDraft();
    dispatch({
      type: 'RESET'
    });
    setShowDraftBanner(false);
    setShowResetDialog(false);
  };
  const updateData = (payload: Partial<OnboardingDraft>) => {
    dispatch({
      type: 'UPDATE_DATA',
      payload
    });
  };
  const validateCurrentStep = () => {
    if (state.stage === 'anunt') {
      switch (state.subStep) {
        case 0:
          return !!state.livingModel;
        case 1:
          return !!state.housingType;
        case 2:
          return !!state.neighborhoods && state.neighborhoods.length > 0 && !!state.budgetEur && state.budgetEur > 0;
        case 3:
          return !!state.moveInDate && !!state.duration;
        case 4:
          return true;
        case 5:
          return true;
      }
    }
    return true;
  };
  const handleNext = () => {
    if (!validateCurrentStep()) return;
    dispatch({
      type: 'NEXT_STEP'
    });
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handlePrev = () => {
    dispatch({
      type: 'PREV_STEP'
    });
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Backward-only stage navigation from the header breadcrumb
  const STAGE_ORDER_IDX: Record<'anunt' | 'profil' | 'cont', number> = {
    anunt: 0,
    profil: 1,
    cont: 2
  };
  const handleJumpToStage = (target: 'anunt' | 'profil' | 'cont') => {
    if (STAGE_ORDER_IDX[target] >= STAGE_ORDER_IDX[state.stage]) return;
    // Jump to the start of the target stage. For 'anunt' specifically, return to the LAST anunt sub-step
    // so the user lands on the most recent place they edited.
    const subStep = target === 'anunt' ? 5 : 0;
    dispatch({
      type: 'SET_STAGE',
      stage: target,
      subStep
    });
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // In listing-only mode, the user finalizes from the last anunț step
  const handlePublishListing = () => {
    saveOnboardingDraft({
      ...state,
      draftedAt: new Date().toISOString()
    });
    updateSession({
      hasListing: true
    });
    setRecentPublish();
    navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
  };
  // Profil → advance to Cont (used in normal flow only)
  const handleProfileSubmit = () => {
    dispatch({
      type: 'NEXT_STEP'
    });
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  // Cont is the FINAL stage in normal flow → finalize everything here
  const handleAccountSubmit = () => {
    const isEmailMethod = state.account?.method === 'email';
    saveOnboardingDraft({
      ...state,
      emailVerified: !isEmailMethod
    });
    setSession({
      name: state.profile?.name || 'Chiriaș',
      email: state.account?.email,
      phone: state.profile?.phone,
      avatar: state.profile?.avatar,
      role: 'demand',
      hasListing: true
    });
    setRecentPublish();
    navigate(PAGE_ROUTE.DASHBOARD_PROFIL);
  };
  const renderStep = () => {
    if (state.stage === 'anunt') {
      switch (state.subStep) {
        case 0:
          return <StepLivingModel data={state} updateData={updateData} onAutoAdvance={handleNext} data-id="element-1761" />;
        case 1:
          return <StepListingType data={state} updateData={updateData} data-id="element-1762" />;
        case 2:
          return <StepLocationBudget data={state} updateData={updateData} data-id="element-1763" />;
        case 3:
          return <StepTiming data={state} updateData={updateData} data-id="element-1764" />;
        case 4:
          return <StepLifestyle data={state} updateData={updateData} data-id="element-1765" />;
        case 5:
          return <StepBioAmenities data={state} updateData={updateData} data-id="element-1766" />;
      }
    }
    // The two stages below are skipped entirely in listing-only mode
    if (state.stage === 'profil') {
      return <StepProfile data={state} updateData={updateData} onSubmit={handleProfileSubmit} data-id="element-1767" />;
    }
    if (state.stage === 'cont') {
      return <StepAccount data={state} updateData={updateData} onSubmit={handleAccountSubmit} data-id="element-1768" />;
    }
    return null;
  };
  const isLastAnuntStep = state.stage === 'anunt' && state.subStep === 5;
  const isPublishStep = listingOnly && isLastAnuntStep;
  const isNextDisabled = !validateCurrentStep();
  const showFooterNav = state.stage === 'anunt';
  const primaryLabel = isPublishStep ? 'Publică anunțul' : 'Continuă';
  const primaryAction = isPublishStep ? handlePublishListing : handleNext;
  const closeDestination = listingOnly ? PAGE_ROUTE.DASHBOARD_PROFIL : PAGE_ROUTE.HOME;
  return <div className="min-h-screen bg-dark-1 flex flex-col" data-id="element-1769">
      <OnboardingHeader stage={state.stage} listingOnly={listingOnly} onReset={handleReset} onClose={() => navigate(closeDestination)} onJumpToStage={handleJumpToStage} data-id="element-1770" />

      <ConfirmDialog open={showResetDialog} title="Începe de la zero?" description="Tot progresul tău va fi șters. Această acțiune nu poate fi anulată." confirmLabel="Da, șterge tot" cancelLabel="Anulează" variant="danger" onConfirm={confirmReset} onCancel={() => setShowResetDialog(false)} data-id="element-1771" />

      <OnboardingProgressBar stage={state.stage} subStep={state.subStep} listingOnly={listingOnly} data-id="element-1772" />

      {showDraftBanner && <div className="bg-lime-5 border-b border-lime-4 p-3 px-4" data-id="element-1773">
          <div className="max-w-xl mx-auto flex items-center justify-between gap-4" data-id="element-1774">
            <p className="text-sm text-lime-1" data-id="element-1775">
              Ai un draft salvat. Vrei să continui?
            </p>
            <div className="flex items-center gap-3" data-id="element-1776">
              <button onClick={handleReset} className="text-xs text-light-3 hover:text-light-1" data-id="element-1777">
                Începe de la zero
              </button>
              <Button size="sm" onClick={() => setShowDraftBanner(false)} className="h-8 bg-lime-1 text-dark-1 hover:bg-lime-2" data-id="element-1778">
                Continuă
              </Button>
            </div>
          </div>
        </div>}

      {/* Main scroll area */}
      <main className="flex-1 flex flex-col w-full" data-id="element-1779">
        <div className="flex-1 w-full max-w-xl mx-auto px-6 py-8 sm:py-16 pb-32 lg:pb-8 flex flex-col" data-id="element-1780">
          <div className="flex-1 flex flex-col justify-center" data-id="element-1781">
            {renderStep()}
          </div>

          {/* Inline desktop navigation right under the form */}
          {showFooterNav && <div className="hidden lg:flex items-center justify-between mt-12 pt-6" data-id="element-1782">
              {state.subStep > 0 ? <Button variant="outline" onClick={handlePrev} className="border-dark-3 text-light-2 hover:bg-dark-2 bg-transparent" data-id="element-1783">
                  Înapoi
                </Button> : <div data-id="element-1784" />}
              <Button onClick={primaryAction} disabled={isNextDisabled} className="bg-lime-1 text-dark-1 hover:bg-lime-2 px-8 disabled:opacity-50" data-id="element-1785">
                {primaryLabel}
              </Button>
            </div>}
        </div>
      </main>

      {/* Sticky bottom nav — mobile only */}
      {showFooterNav && <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-dark-1/90 backdrop-blur-md border-t border-dark-3 p-4 z-40" data-id="element-1786">
          <div className="max-w-xl mx-auto flex items-center justify-between gap-3" data-id="element-1787">
            {state.subStep > 0 ? <Button variant="outline" onClick={handlePrev} className="border-dark-3 text-light-2 hover:bg-dark-2 bg-transparent" data-id="element-1788">
                Înapoi
              </Button> : <div data-id="element-1789" />}
            <Button onClick={primaryAction} disabled={isNextDisabled} className="bg-lime-1 text-dark-1 hover:bg-lime-2 px-8 disabled:opacity-50" data-id="element-1790">
              {primaryLabel}
            </Button>
          </div>
        </div>}
    </div>;
}